// API-based managers for OrderTrack Pro
// Replaces localStorage-based mock.js managers

import { orderService, brandSpecService, userService } from './api.js';

// =============== ORDER MANAGER ===============

export const orderManagerAPI = {
  orders: [],
  
  async getAllOrders() {
    try {
      this.orders = await orderService.getAllOrders();
      return this.orders;
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  },

  async getOrderById(id) {
    try {
      return await orderService.getOrderById(id);
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  },

  async addOrder(orderData) {
    try {
      const newOrder = await orderService.createOrder(orderData);
      this.orders.unshift(newOrder);
      return newOrder;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  async updateOrder(id, orderData) {
    try {
      const updatedOrder = await orderService.updateOrder(id, orderData);
      const index = this.orders.findIndex(order => order.id === id);
      if (index !== -1) {
        this.orders[index] = updatedOrder;
      }
      return updatedOrder;
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },

  async deleteOrder(id) {
    try {
      await orderService.deleteOrder(id);
      this.orders = this.orders.filter(order => order.id !== id);
      return { success: true, message: 'Order deleted successfully' };
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  },

  // Helper methods for compatibility with existing frontend code
  getStats() {
    const totalOrders = this.orders.length;
    const pendingOrders = this.orders.filter(o => o.status === 'Pending').length;
    const completeOrders = this.orders.filter(o => o.status === 'Complete').length;
    const totalValue = this.orders.reduce((sum, order) => sum + (order.price * order.quantity), 0);
    const pendingValue = this.orders
      .filter(o => o.status === 'Pending')
      .reduce((sum, order) => sum + (order.price * order.balance), 0);
    
    return {
      totalOrders,
      pendingOrders,
      completeOrders,
      totalValue,
      pendingValue
    };
  },

  // Search functionality
  searchOrders(query) {
    const searchTerm = query.toLowerCase();
    return this.orders.filter(order => 
      order.brandName.toLowerCase().includes(searchTerm) ||
      order.orderNumber.toLowerCase().includes(searchTerm) ||
      (order.poNumber && order.poNumber.toLowerCase().includes(searchTerm))
    );
  }
};

// =============== BRAND SPECS MANAGER ===============

export const brandSpecsManagerAPI = {
  brands: [],

  async getAllBrands() {
    try {
      this.brands = await brandSpecService.getAllBrandSpecs();
      return this.brands;
    } catch (error) {
      console.error('Error fetching brand specs:', error);
      return [];
    }
  },

  async getBrandById(brandId) {
    try {
      return await brandSpecService.getBrandSpecById(brandId);
    } catch (error) {
      console.error('Error fetching brand spec:', error);
      return null;
    }
  },

  async getBrandByName(brandName) {
    try {
      return await brandSpecService.getBrandSpecByName(brandName);
    } catch (error) {
      console.error('Error fetching brand spec by name:', error);
      return null;
    }
  },

  async getBrandByBrandId(brandId) {
    // Alias for getBrandById for compatibility
    return await this.getBrandById(brandId);
  },

  async addBrand(brandData) {
    try {
      const newBrand = await brandSpecService.createBrandSpec(brandData);
      this.brands.unshift(newBrand);
      return newBrand;
    } catch (error) {
      console.error('Error creating brand spec:', error);
      throw error;
    }
  },

  async updateBrand(brandId, brandData) {
    try {
      const updatedBrand = await brandSpecService.updateBrandSpec(brandId, brandData);
      const index = this.brands.findIndex(brand => brand.brandId === brandId);
      if (index !== -1) {
        this.brands[index] = updatedBrand;
      }
      return updatedBrand;
    } catch (error) {
      console.error('Error updating brand spec:', error);
      throw error;
    }
  },

  async deleteBrand(brandId) {
    try {
      await brandSpecService.deleteBrandSpec(brandId);
      this.brands = this.brands.filter(brand => brand.brandId !== brandId);
      return { success: true, message: 'Brand specification deleted successfully' };
    } catch (error) {
      console.error('Error deleting brand spec:', error);
      throw error;
    }
  },

  async getBrandNames() {
    try {
      return await brandSpecService.getBrandNames();
    } catch (error) {
      console.error('Error fetching brand names:', error);
      return [];
    }
  },

  // Search functionality
  searchBrands(query) {
    const searchTerm = query.toLowerCase();
    return this.brands.filter(brand => 
      brand.brandName.toLowerCase().includes(searchTerm) ||
      brand.buyerCompany.toLowerCase().includes(searchTerm) ||
      (brand.fabricVariant && brand.fabricVariant.toLowerCase().includes(searchTerm)) ||
      (brand.fabricColor && brand.fabricColor.toLowerCase().includes(searchTerm))
    );
  },

  // Get statistics
  getStats() {
    const totalBrands = this.brands.length;
    const activeBrands = this.brands.filter(b => b.status === 'Active').length;
    const inactiveBrands = this.brands.filter(b => b.status === 'Inactive').length;
    
    // Get unique companies and material types
    const uniqueCompanies = [...new Set(this.brands.map(b => b.buyerCompany))];
    const materialTypes = [...new Set(this.brands.map(b => b.printingMaterial).filter(Boolean))];
    
    return {
      totalBrands,
      activeBrands,
      inactiveBrands,
      uniqueCompanies,
      materialTypes
    };
  },

  // Download functionality
  async downloadAllBrandsPDF() {
    try {
      // Dynamic import to avoid bundling issues
      const jsPDF = (await import('jspdf')).jsPDF;
      const brands = this.brands;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = 297; // A4 height in mm
      const margin = 20;
      let yPosition = margin;
      
      // Title
      pdf.setFontSize(20);
      pdf.text('Brand Specifications Report', margin, yPosition);
      yPosition += 15;
      
      // Date
      pdf.setFontSize(12);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += 10;
      
      // Summary
      pdf.text(`Total Brands: ${brands.length}`, margin, yPosition);
      yPosition += 20;
      
      // Brand details
      brands.forEach((brand, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.setFontSize(16);
        pdf.text(`${index + 1}. ${brand.brandName} (${brand.brandCode || 'N/A'})`, margin, yPosition);
        yPosition += 10;
        
        pdf.setFontSize(11);
        const details = [
          `Company: ${brand.buyerCompany || 'N/A'}`,
          `Material: ${brand.printingMaterial || 'N/A'}`,
          `Color: ${brand.fabricColor || 'N/A'}`,
          `Variant: ${brand.fabricVariant || 'N/A'}`
        ];
        
        details.forEach(detail => {
          pdf.text(detail, margin + 5, yPosition);
          yPosition += 6;
        });
        
        yPosition += 10;
      });
      
      pdf.save(`brand-specifications-${new Date().toISOString().split('T')[0]}.pdf`);
      return { success: true, message: 'Brand specifications PDF downloaded successfully' };
    } catch (error) {
      console.error('Error generating PDF:', error);
      return { success: false, message: 'Failed to generate PDF: ' + error.message };
    }
  },

  downloadAllBrands() {
    try {
      const dataStr = JSON.stringify(this.brands, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brand-specifications-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return { success: true, message: 'Brand specifications downloaded successfully' };
    } catch (error) {
      console.error('Error downloading brands:', error);
      return { success: false, message: 'Failed to download brands: ' + error.message };
    }
  },

  async downloadBrandPDF(brandId) {
    try {
      const jsPDF = (await import('jspdf')).jsPDF;
      const brand = this.brands.find(b => b.id === brandId);
      
      if (!brand) {
        return { success: false, message: 'Brand not found' };
      }
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 20;
      let yPosition = margin;
      
      // Title
      pdf.setFontSize(20);
      pdf.text(`${brand.brandName} Specification`, margin, yPosition);
      yPosition += 15;
      
      // Brand details
      pdf.setFontSize(12);
      const details = [
        `Brand Code: ${brand.brandCode || 'N/A'}`,
        `Company: ${brand.buyerCompany || 'N/A'}`,
        `Material: ${brand.printingMaterial || 'N/A'}`,
        `Color: ${brand.fabricColor || 'N/A'}`,
        `Variant: ${brand.fabricVariant || 'N/A'}`,
        `Generated: ${new Date().toLocaleDateString()}`
      ];
      
      details.forEach(detail => {
        pdf.text(detail, margin, yPosition);
        yPosition += 8;
      });
      
      pdf.save(`${brand.brandName}-${brand.brandCode || 'spec'}.pdf`);
      return { success: true, message: 'Brand specification PDF downloaded successfully' };
    } catch (error) {
      console.error('Error generating brand PDF:', error);
      return { success: false, message: 'Failed to generate PDF: ' + error.message };
    }
  },

  downloadBrandById(brandId) {
    try {
      const brand = this.brands.find(b => b.id === brandId);
      if (!brand) {
        return { success: false, message: 'Brand not found' };
      }
      
      const dataStr = JSON.stringify(brand, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${brand.brandName}-${brand.brandCode || 'spec'}-specs.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return { success: true, message: 'Brand specification downloaded successfully' };
    } catch (error) {
      console.error('Error downloading brand:', error);
      return { success: false, message: 'Failed to download brand: ' + error.message };
    }
  }
};

// =============== AUTH MANAGER ===============

export const authManagerAPI = {
  currentUser: null,

  async login(username, password) {
    try {
      const response = await userService.login(username, password);
      this.currentUser = response.user;
      // Store in sessionStorage for persistence across page refreshes
      sessionStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      return { success: true, user: this.currentUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  },

  logout() {
    this.currentUser = null;
    sessionStorage.removeItem('currentUser');
  },

  getCurrentUser() {
    if (!this.currentUser) {
      // Try to restore from sessionStorage
      const storedUser = sessionStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUser = JSON.parse(storedUser);
      }
    }
    return this.currentUser;
  },

  isAuthenticated() {
    return this.getCurrentUser() !== null;
  },

  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.role === role;
  }
};

// =============== UTILITY FUNCTIONS ===============

// Format currency (same as original)
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date (same as original)
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN');
};

// Format quantity (same as original)  
export const formatQuantity = (quantity, unit = 'pieces') => {
  return `${quantity.toLocaleString()} ${unit}`;
};

// Notification manager placeholder (would need WebSocket implementation)
export const orderNotificationsManagerAPI = {
  notifications: [],
  
  addNotification(notification) {
    this.notifications.unshift(notification);
    console.log('Notification added:', notification);
  },

  getNotifications() {
    return this.notifications;
  },

  markAsRead(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.read = true;
    }
  }
};

// Export all managers
export default {
  orderManager: orderManagerAPI,
  brandSpecsManager: brandSpecsManagerAPI,  
  authManager: authManagerAPI,
  orderNotificationsManager: orderNotificationsManagerAPI,
  formatCurrency,
  formatDate,
  formatQuantity
};